import { FC, useRef } from "react";
import { Button, Heading, Sheet } from "@components";
import classNames from "classnames";
import { motion } from "framer-motion";

import useCustomScrollbar from "../../../../hooks/useCustomScrollbar";
import useVisibility from "../../../../hooks/useVisibility";
import { useProductContext } from "../../../context/ProductContext";
import { addHeapModalOpened } from "../../../services/elevar/events";
import type { FormattedOptionModal } from "../../../services/util/optionRendering";
import { RichText } from "../..";
import CloseButton from "../../Cart/components/CloseButton";
import { getActiveGroup } from "../util/groups";

import type { ClassicGroupPickerProps } from "./ClassicGroupPickerV2";
import type { OptionPickerProps } from "./OptionPicker";
import type { RichGroupProps } from "./RichGroupV2";

interface Props extends FormattedOptionModal {
  richGroupPicker: FC<RichGroupProps>;
  groupPicker: FC<ClassicGroupPickerProps>;
  optionPicker: FC<OptionPickerProps>;
}

export default function OptionModal({
  name,
  title,
  content,
  buttonText,
  options,
  optionImages,
  optionValueReplacement,
  richGroupPicker: RichGroup,
  groupPicker,
  optionPicker,
}: Props) {
  const { isSheetVisible, toggleSheet, hideSheet } = useVisibility(
    "Sheet",
    false,
    {
      onShow() {
        // fire heap event to track that modal was opened
        addHeapModalOpened(name, buttonText);
      },
    }
  );

  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const scrollTrackRef = useRef<HTMLDivElement>(null);
  const { thumbWidth, x, shouldScroll } = useCustomScrollbar(
    scrollContainerRef,
    scrollTrackRef,
    isSheetVisible
  );

  const { selectedOptions: externalOptions } = useProductContext();

  return (
    <div>
      <Button
        variant="underline"
        className="text-slate hocus:text-charcoal !mb-0 whitespace-nowrap"
        onClick={toggleSheet}
      >
        {buttonText}
      </Button>

      <Sheet
        side="right"
        isOpen={isSheetVisible}
        onDismiss={hideSheet}
        className="!p-0 !w-auto max-w-full md:max-w-[600px]"
        top
        unmount={false}
      >
        <div
          className={classNames(
            "flex items-center gap-2 justify-between sticky top-0 bg-white",
            "pt-8 pb-6 px-6 md:px-10 z-20",
            title ? "justify-between" : "justify-end"
          )}
        >
          {title && (
            <Heading as="h2" variant="h4" className="mt-6 md:mt-0">
              {title}
            </Heading>
          )}

          <CloseButton onClick={hideSheet} className="-mt-8 md:mt-0" />

          <div className="absolute bottom-0 h-px inset-x-6 md:inset-x-10 bg-lightgray" />
        </div>

        <div className="flex flex-col gap-6 px-6 pt-3 pb-10 leading-normal md:py-6 md:px-10">
          <div className="text-[18px]">
            <RichText richTextResponse={content} />
          </div>

          <div className="flex flex-col gap-4">
            {options.map((option) => {
              const externalValue =
                externalOptions[option.name]
                  ?.replace(optionValueReplacement, "")
                  .trim() ?? "";

              const activeGroup = externalValue
                ? getActiveGroup(externalValue, option.groups)
                : null;
              const groups =
                option.groups && activeGroup
                  ? [
                      activeGroup,
                      ...option.groups.filter(
                        (g) => g.name !== activeGroup.name
                      ),
                    ]
                  : option.groups;

              return (
                <div
                  key={`option-modal-${option.name}`}
                  className="relative flex flex-col gap-8"
                >
                  <div
                    ref={scrollContainerRef}
                    className="flex gap-6 px-6 pb-6 -mx-6 overflow-x-auto md:px-10 md:-mx-10 no-scrollbar scroll-px-6 md:scroll-px-10"
                  >
                    {groups?.map((group) => (
                      <RichGroup
                        key={group.name}
                        option={option}
                        optionImages={optionImages}
                        optionValueReplacement={optionValueReplacement}
                        group={group}
                        groupPicker={groupPicker}
                        optionPicker={optionPicker}
                        inModal
                      />
                    ))}
                  </div>

                  {/* Custom scrollbar */}
                  <div
                    ref={scrollTrackRef}
                    className={classNames(
                      "w-3/4 h-0.5 mx-auto bg-lightgray transition",
                      {
                        "opacity-0": !shouldScroll,
                      }
                    )}
                  >
                    <motion.div
                      className="h-full bg-pine"
                      style={{
                        width: thumbWidth,
                        x,
                      }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </Sheet>
    </div>
  );
}
